import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import { startCase } from "lodash";
import {
  Box,
  Typography,
  Alert,
  Stack,
  Pagination,
  CircularProgress,
  IconButton,
  Button,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  InputAdornment,
  Popover,
  MenuItem,
} from "@mui/material";
import {
  AttachFile as AttachFileIcon,
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon,
  FilterAlt as FilterAltIcon,
} from "@mui/icons-material";

const locationConfig = {
  sign: {
    borderLeft: "3px solid #1976d2",
    chipColor: "primary",
    chipLabel: "Auto • Sign",
  },
  create: {
    borderLeft: "3px solid #2e7d32",
    chipColor: "success",
    chipLabel: "Manual • Create",
  },
};

const getLocationStyle = (location) =>
  locationConfig[location] ?? { borderLeft: "1px solid #e0e0e0" };

const flattenFieldResults = (fields = []) =>
  fields.flatMap((field) => [
    field,
    ...(field?.children || []).map((child) => ({
      ...child,
      parentLabel: field?.field_label || field?.field_name,
      parentSearchQuery: field?.field_name || field?.field_label,
    })),
  ]);

const DefualtFormFieldPanel = ({
  currentProcessingFile,
  setCurrentProcessingFile,
  onNotify,
  loading: loadingDoc,
  getFormFieldsList,
  fieldNavigationTarget,
  onFieldNavigationComplete,
  onNavigateToField,
}) => {
  const currentSession = useSelector((state) => state.sessions.currentSession);

  const [fields, setFields] = useState([]);
  const [searchField, setSearchField] = useState("");
  const [matchingFormFields, setMatchingFormFields] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [totalPage, setTotalPage] = useState(0);
  const [createFieldMessage, setCreateFieldMessage] = useState({
    type: "",
    message: "",
  });

  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [crossTabResultsAnchorEl, setCrossTabResultsAnchorEl] = useState(null);
  const [filter, setFilter] = useState({});
  const [activeCategoryLabel, setActiveCategoryLabel] = useState("");
  const [expandedCategoryIds, setExpandedCategoryIds] = useState(
    () => new Set(),
  );
  const [expandedFieldIds, setExpandedFieldIds] = useState(() => new Set());
  const [scrollTarget, setScrollTarget] = useState(null);

  const debounceTimerRef = useRef(null);
  const pendingNavigationRequestRef = useRef(null);
  const fieldAccordionRefs = useRef(new Map());

  useEffect(() => {
    if (error) {
      const interv = setTimeout(() => {
        setError(false);
      }, 5000);
      return () => clearTimeout(interv);
    }
  }, [error]);

  useEffect(() => {
    if (createFieldMessage) {
      const interv = setTimeout(() => {
        setCreateFieldMessage({
          type: "",
          message: "",
        });
      }, 5000);
      return () => clearTimeout(interv);
    }
  }, [createFieldMessage]);

  const getFormFields = useCallback(
    async (filter, page, searchQuery) => {
      setLoading(true);
      setError("");

      try {
        const res = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/nocApi/form-fields/tree`,
          {
            currentSession,
            ...filter,
            page: 1,
            limit: 7,
            default_page: page,
            default_limit: 7,
            query: searchQuery,
          },
          { withCredentials: true },
        );

        if (res.data.success) {
          const fields = res.data.data?.default_fields?.fields ?? [];
          setFields(fields);
          setTotalPage(res.data.data?.default_fields?.total_pages ?? 0);
          setMatchingFormFields(
            searchQuery?.trim()
              ? flattenFieldResults(res.data.data?.regular_fields?.fields ?? [])
              : [],
          );
        }
      } catch (error) {
        setMatchingFormFields([]);
        setError(
          error?.response?.data?.message ||
            "An error occurred while fetching fields",
        );
      } finally {
        setLoading(false);
      }
    },
    [currentSession],
  );

  const handlePageChange = (e, value) => {
    setPage(value);
    getFormFields(filter, value, searchField);
  };

  useEffect(() => {
    if (
      fieldNavigationTarget?.tabValue === "3" &&
      fieldNavigationTarget?.fieldId
    ) {
      return;
    }

    getFormFields({}, 1, "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      fieldNavigationTarget?.tabValue !== "3" ||
      !fieldNavigationTarget?.fieldId
    ) {
      return;
    }

    pendingNavigationRequestRef.current = fieldNavigationTarget.requestId;
    setSearchField(fieldNavigationTarget.searchQuery || "");
    setPage(1);
    getFormFields({}, 1, fieldNavigationTarget.searchQuery || "");
  }, [fieldNavigationTarget, getFormFields]);

  useEffect(() => {
    if (
      loading ||
      !fieldNavigationTarget?.fieldId ||
      pendingNavigationRequestRef.current !== fieldNavigationTarget.requestId
    ) {
      return;
    }

    const targetId = fieldNavigationTarget.fieldId;
    let targetCategoryId = null;
    let targetPath = null;

    for (const field of fields || []) {
      const categoryId = String(field?.category_id ?? "uncategorized");

      if (field?.field_id === targetId) {
        targetCategoryId = categoryId;
        targetPath = [field.field_id];
        break;
      }

      const child = field?.children?.find(
        (option) => option?.field_id === targetId,
      );
      if (child) {
        targetCategoryId = categoryId;
        targetPath = [field.field_id, child.field_id];
        break;
      }
    }

    if (!targetCategoryId || !targetPath) return;

    setExpandedCategoryIds((previousIds) => {
      const nextIds = new Set(previousIds);
      nextIds.add(targetCategoryId);
      return nextIds;
    });
    setExpandedFieldIds((previousIds) => {
      const nextIds = new Set(previousIds);
      targetPath.forEach((fieldId) => nextIds.add(fieldId));
      return nextIds;
    });
    setScrollTarget({
      fieldId: targetId,
      requestId: fieldNavigationTarget.requestId,
    });
    pendingNavigationRequestRef.current = null;
  }, [fieldNavigationTarget, fields, loading]);

  useEffect(() => {
    if (!scrollTarget?.fieldId) return;

    const timerId = window.setTimeout(() => {
      fieldAccordionRefs.current.get(scrollTarget.fieldId)?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      onFieldNavigationComplete?.(scrollTarget.requestId);
      setScrollTarget(null);
    }, 350);

    return () => window.clearTimeout(timerId);
  }, [
    expandedCategoryIds,
    expandedFieldIds,
    onFieldNavigationComplete,
    scrollTarget,
  ]);

  const handleCategoryAccordionChange = (categoryId) => (event, isExpanded) => {
    setExpandedCategoryIds((previousIds) => {
      const nextIds = new Set(previousIds);

      if (isExpanded) {
        nextIds.add(categoryId);
      } else {
        nextIds.delete(categoryId);
      }

      return nextIds;
    });
  };

  const handleFieldAccordionChange = (fieldId) => (event, isExpanded) => {
    setExpandedFieldIds((previousIds) => {
      const nextIds = new Set(previousIds);

      if (isExpanded) {
        nextIds.add(fieldId);
      } else {
        nextIds.delete(fieldId);
      }

      return nextIds;
    });
  };

  const handleAttach = (e, field) => {
    e.stopPropagation();

    const tempFields = currentProcessingFile?.document?.tempFields;

    if (!tempFields?.selectedExtraFields?.length) {
      onNotify?.(
        "error",
        `Select a field from the PDF first, then you'll be able to assign it.`,
      );
      return;
    }

    if (tempFields?.selectedExtraFields?.length > 1) {
      onNotify?.(
        "error",
        `You have selected more than one field. Only a single field can be assigned at a time.`,
      );
      return;
    }

    const selectedField = tempFields?.selectedExtraFields?.[0] ?? {};

    const updatedFormFields = tempFields?.formFields?.map((f) => {
      if (selectedField?.name === f?.name) {
        return {
          ...f,
          field_id: field?.field_id,
          location: field?.location || "create",
          ...(field?.field_type === "percentageCheckbox" && { hidden: false }),
        };
      } else {
        return f;
      }
    });
    const updatedSelectedFields = [
      {
        ...selectedField,
        field_id: field?.field_id,
        location: field?.location || "create",
        ...(field?.field_type === "percentageCheckbox" && { hidden: false }),
      },
    ];

    setCurrentProcessingFile((prevFile) => ({
      ...prevFile,
      document: {
        ...prevFile.document,
        tempFields: {
          ...prevFile.document.tempFields,
          formFields: updatedFormFields,
          selectedExtraFields: updatedSelectedFields,
        },
      },
    }));
  };

  const handleChange = (e) => {
    const val = e.target.value;
    setSearchField(val);
    setCrossTabResultsAnchorEl(null);
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      getFormFields(filter, 1, val.trim());
      setPage(1);
    }, 1000);
  };

  const handleShowAllCategoryFields = async (
    event,
    categoryId,
    categoryLabel,
  ) => {
    event.stopPropagation();

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }

    const categoryFilter = { ...filter, category_id: categoryId };

    setExpandedCategoryIds((previousIds) => {
      const nextIds = new Set(previousIds);
      nextIds.add(categoryId);
      return nextIds;
    });
    setFilter(categoryFilter);
    setActiveCategoryLabel(categoryLabel || "Selected section");
    setSearchField("");
    setPage(1);

    setLoading(true);
    setError("");

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/form-fields/list`,
        { currentSession },
        { withCredentials: true },
      );

      if (res.data.success) {
        const categoryFields = (res.data.data?.fields ?? []).filter((field) => {
          const isCurrentCategory =
            String(field?.category_id ?? "uncategorized") ===
            String(categoryId);
          const matchesLocation =
            !filter?.location || field?.location === filter.location;

          return field?.is_default && isCurrentCategory && matchesLocation;
        });

        setFields(categoryFields);
        setTotalPage(categoryFields.length > 0 ? 1 : 0);
      }
    } catch (error) {
      setError(
        error?.response?.data?.message ||
          "An error occurred while fetching fields",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClearCategoryScope = () => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }

    const activeFilter = { ...filter };
    delete activeFilter.category_id;

    setFilter(activeFilter);
    setActiveCategoryLabel("");
    setSearchField("");
    setPage(1);
    getFormFields(activeFilter, 1, "");
  };

  const handleClickFilter = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleCloseFilter = () => {
    setFilterAnchorEl(null);
  };

  const fieldsByCategory = useMemo(() => {
    return fields?.reduce((acc, field) => {
      const categoryId = field.category_id ?? "uncategorized";

      if (!acc[categoryId]) {
        acc[categoryId] = {
          categoryLabel: field.category_label ?? "Uncategorized",
          fields: [],
        };
      }

      acc[categoryId].fields.push(field);

      return acc;
    }, {});
  }, [fields]);

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flexShrink: 0,
          height: "calc(100% - 50px)",
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.25,
            borderBottom: "1px solid #F3F4F6",
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
          }}
        >
          <Box>
            <Typography
              sx={{ fontSize: 16, fontWeight: 600, color: "#111827" }}
            >
              Default Form Fields
            </Typography>
          </Box>
        </Box>

        <Box
          display={"flex"}
          alignItems={"center"}
          gap={1}
          sx={{ px: 1.5, pb: 1.25, mt: 1.25 }}
        >
          <TextField
            id="searchInput"
            value={searchField}
            onChange={(e) => handleChange(e)}
            size="small"
            placeholder="Search assigned fields..."
            sx={{
              "& .MuiInputBase-input": { fontSize: 14 },
              flex: 1,
            }}
            InputProps={{
              sx: { padding: "0px 10px" },
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            variant="outlined"
          />
          <IconButton
            size="small"
            onClick={handleClickFilter}
            disabled={loadingDoc}
            sx={{
              border: "1px solid #d0d7de",
              borderRadius: 1.5,
              backgroundColor: "transparent",
              p: "4.41px !important",
            }}
          >
            <FilterAltIcon
              sx={{
                fontSize: "0.95rem",
              }}
            />
          </IconButton>
        </Box>
        {filter?.category_id != null && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-start",
              px: 1.5,
              pb: 1.25,
            }}
          >
            <Chip
              label={`Showing: ${activeCategoryLabel || "Selected section"}`}
              size="small"
              onDelete={handleClearCategoryScope}
              disabled={loading || loadingDoc}
              color="primary"
              variant="outlined"
              sx={{
                maxWidth: "100%",
                "& .MuiChip-label": {
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                },
              }}
            />
          </Box>
        )}
        {matchingFormFields.length > 0 && (
          <>
            <Button
              size="small"
              variant="outlined"
              onClick={(event) =>
                setCrossTabResultsAnchorEl(event.currentTarget)
              }
              sx={{
                mx: 1.5,
                mb: 1.25,
                px: 1.25,
                justifyContent: "space-between",
                textTransform: "none",
                fontSize: 12,
              }}
            >
              {matchingFormFields.length} matching{" "}
              {matchingFormFields.length === 1 ? "field" : "fields"} in Form
              Fields
              <Box component="span" sx={{ ml: 1, fontSize: 16 }}>
                ›
              </Box>
            </Button>
            <Popover
              open={Boolean(crossTabResultsAnchorEl)}
              anchorEl={crossTabResultsAnchorEl}
              onClose={() => setCrossTabResultsAnchorEl(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
              transformOrigin={{ vertical: "top", horizontal: "left" }}
              PaperProps={{
                sx: {
                  width: 300,
                  maxWidth: "calc(100vw - 32px)",
                  maxHeight: 280,
                },
              }}
            >
              <Box sx={{ px: 1.5, pt: 1.25, pb: 0.5 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                  Matching Form Fields
                </Typography>
                <Typography color="text.secondary" sx={{ fontSize: 11 }}>
                  Select a field to open it in the Form Fields tab.
                </Typography>
              </Box>
              <Stack sx={{ px: 0.75, pb: 0.75 }}>
                {matchingFormFields.map((field) => (
                  <Button
                    key={field?.field_id}
                    size="small"
                    variant="text"
                    onClick={() => {
                      setCrossTabResultsAnchorEl(null);
                      onNavigateToField?.({
                        fieldId: field?.field_id,
                        searchQuery:
                          field?.parentSearchQuery ||
                          field?.field_name ||
                          field?.field_label ||
                          searchField,
                        tabValue: "2",
                      });
                    }}
                    sx={{
                      justifyContent: "flex-start",
                      textAlign: "left",
                      textTransform: "none",
                      px: 1,
                      py: 0.75,
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontSize: 12,
                          fontWeight: 500,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {field?.field_label || field?.field_name}
                      </Typography>
                      {field?.parentLabel && (
                        <Typography
                          color="text.secondary"
                          sx={{
                            fontSize: 10,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {field.parentLabel}
                        </Typography>
                      )}
                    </Box>
                  </Button>
                ))}
              </Stack>
            </Popover>
          </>
        )}

        {(error || createFieldMessage?.message) && (
          <Alert
            severity={error ? "error" : createFieldMessage?.type}
            sx={{ borderRadius: 2, mx: 1.5, mb: 1.25 }}
          >
            {error || createFieldMessage?.message}
          </Alert>
        )}

        {loading ? (
          <Box
            sx={{ px: 1.5, display: "flex", justifyContent: "center", mt: 2.5 }}
          >
            <CircularProgress color="warning" size="3rem" />
          </Box>
        ) : fields?.length === 0 ? (
          <Box sx={{ px: 1.5 }}>
            <Typography
              color="primary"
              variant="h6"
              mb={2}
              mt={2.5}
              sx={{ fontSize: "1rem" }}
              align="center"
            >
              You have no fields to display just yet
            </Typography>
          </Box>
        ) : (
          <>
            <Box flex={1} sx={{ px: 1.5, overflow: "auto" }}>
              <Stack gap={0.75}>
                {Object.entries(fieldsByCategory).map(
                  ([categoryId, categoryData]) => (
                    <Accordion
                      key={categoryId}
                      expanded={expandedCategoryIds.has(categoryId)}
                      onChange={handleCategoryAccordionChange(categoryId)}
                      disableGutters
                      elevation={0}
                      sx={{
                        border: "1px solid #e0e0e0",
                        borderRadius: "12px !important",
                        overflow: "hidden",
                        borderLeft: "3px solid #1976d2",
                        "&:before": { display: "none" },
                        "&.Mui-expanded": {
                          boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                        },
                      }}
                    >
                      <AccordionSummary
                        expandIcon={
                          <ExpandMoreIcon sx={{ fontSize: "1.5rem" }} />
                        }
                        sx={{
                          gap: 2,
                        }}
                        slotProps={{
                          content: {
                            sx: {
                              display: "flex",
                              flexDirection: "column",
                            },
                          },
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 1,
                          }}
                        >
                          <Typography
                            variant="body1"
                            sx={{
                              fontSize: "0.85rem",
                              color: "rgba(0, 0, 0, 0.8)",
                              fontWeight: 500,
                            }}
                          >
                            {categoryData?.categoryLabel}
                          </Typography>
                          {searchField.trim() && (
                            <Button
                              variant="text"
                              size="small"
                              disabled={loading || loadingDoc}
                              onClick={(event) =>
                                handleShowAllCategoryFields(
                                  event,
                                  categoryId,
                                  categoryData?.categoryLabel,
                                )
                              }
                              sx={{
                                flexShrink: 0,
                                minWidth: "auto",
                                px: 0.75,
                                textTransform: "none",
                                fontSize: "0.72rem",
                              }}
                            >
                              Show all fields
                            </Button>
                          )}
                        </Box>
                      </AccordionSummary>

                      <AccordionDetails
                        sx={{
                          px: 1.5,
                          pb: 1.5,
                          pt: 0,
                        }}
                      >
                        <Box
                          sx={{
                            maxHeight: "280px",
                            overflowY: "auto",
                            overflowX: "hidden",
                            pr: 0.5,
                            minHeight: 0,
                            overscrollBehavior: "contain",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 0.5,
                            }}
                          >
                            {categoryData?.fields?.map((field, index) => (
                              <Accordion
                                key={field?.field_id}
                                ref={(node) => {
                                  if (node) {
                                    fieldAccordionRefs.current.set(
                                      field?.field_id,
                                      node,
                                    );
                                  } else {
                                    fieldAccordionRefs.current.delete(
                                      field?.field_id,
                                    );
                                  }
                                }}
                                expanded={expandedFieldIds.has(field?.field_id)}
                                onChange={handleFieldAccordionChange(
                                  field?.field_id,
                                )}
                                disableGutters
                                elevation={0}
                                sx={{
                                  border: "1px solid #e0e0e0",
                                  borderRadius: "8px !important",
                                  overflow: "hidden",
                                  borderLeft: getLocationStyle(field?.location)
                                    .borderLeft,
                                  "&:before": { display: "none" },
                                  "&.Mui-expanded": {
                                    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                                  },
                                }}
                              >
                                <AccordionSummary
                                  component="div"
                                  expandIcon={
                                    <ExpandMoreIcon
                                      sx={{ fontSize: "1.2rem" }}
                                    />
                                  }
                                  sx={{
                                    gap: 1.5,
                                    minHeight: "36px !important",
                                  }}
                                  slotProps={{
                                    content: {
                                      sx: {
                                        display: "flex",
                                        flexDirection: "column",
                                        my: "8px !important",
                                      },
                                    },
                                  }}
                                >
                                  <Box
                                    display={"flex"}
                                    justifyContent="space-between"
                                    alignItems="center"
                                  >
                                    <Box>
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          fontSize: "0.78rem",
                                          color: "rgba(0, 0, 0, 0.8)",
                                          fontWeight: 500,
                                          lineHeight: 1.4,
                                        }}
                                      >
                                        {field?.field_label}
                                      </Typography>
                                      {field?.location && (
                                        <Chip
                                          label={startCase(field.location)}
                                          size="small"
                                          variant="filled"
                                          color={
                                            getLocationStyle(field.location)
                                              .chipColor
                                          }
                                          sx={{
                                            fontSize: "0.7rem",
                                            height: 20,
                                            borderRadius: 1,
                                          }}
                                        />
                                      )}
                                    </Box>
                                    <IconButton
                                      size="small"
                                      onClick={(e) => handleAttach(e, field)}
                                      sx={{
                                        border: "1px solid #d0d7de",
                                        borderRadius: 1.5,
                                        backgroundColor: "transparent",
                                        p: "4.41px !important",
                                      }}
                                      disabled={loadingDoc}
                                    >
                                      <AttachFileIcon
                                        sx={{
                                          fontSize: "0.95rem",
                                          transform: "rotate(45deg)",
                                        }}
                                      />
                                    </IconButton>
                                  </Box>
                                </AccordionSummary>

                                <AccordionDetails>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      gap: 0.75,
                                      flexWrap: "wrap",
                                      alignItems: "center",
                                      justifyContent: "space-between",
                                    }}
                                  >
                                    <Box
                                      display={"flex"}
                                      alignItems={"center"}
                                      gap={1}
                                    >
                                      {field?.field_type && (
                                        <Chip
                                          label={startCase(field.field_type)}
                                          size="small"
                                          variant="filled"
                                          sx={{
                                            fontSize: "0.7rem",
                                            height: 20,
                                            borderRadius: 1,
                                          }}
                                        />
                                      )}
                                    </Box>
                                  </Box>
                                  {["checkboxGroup", "selectGroup"].includes(
                                    field?.field_type,
                                  ) &&
                                    field?.children?.length > 0 && (
                                      <Box
                                        sx={{
                                          mt: 1.25,
                                          pt: 1.25,
                                          borderTop: "1px dashed #e0e0e0",
                                        }}
                                      >
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            fontSize: "0.7rem",
                                            color: "rgba(0, 0, 0, 0.45)",
                                            fontWeight: 600,
                                            textTransform: "uppercase",
                                            letterSpacing: "0.04em",
                                            mb: 0.75,
                                            display: "block",
                                          }}
                                        >
                                          Options
                                        </Typography>

                                        <Box
                                          sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: 0.5,
                                            pr: 0.5,
                                          }}
                                        >
                                          {field?.children.map(
                                            (option, index) => (
                                              <Accordion
                                                key={option?.field_id ?? index}
                                                ref={(node) => {
                                                  if (node) {
                                                    fieldAccordionRefs.current.set(
                                                      option?.field_id,
                                                      node,
                                                    );
                                                  } else {
                                                    fieldAccordionRefs.current.delete(
                                                      option?.field_id,
                                                    );
                                                  }
                                                }}
                                                expanded={expandedFieldIds.has(
                                                  option?.field_id,
                                                )}
                                                onChange={handleFieldAccordionChange(
                                                  option?.field_id,
                                                )}
                                                disableGutters
                                                elevation={0}
                                                sx={{
                                                  border: "1px solid #e0e0e0",
                                                  borderRadius:
                                                    "8px !important",
                                                  overflow: "hidden",
                                                  "&:before": {
                                                    display: "none",
                                                  },
                                                  "&.Mui-expanded": {
                                                    boxShadow:
                                                      "0 2px 8px rgba(0,0,0,0.08)",
                                                  },
                                                }}
                                              >
                                                <AccordionSummary
                                                  expandIcon={
                                                    <ExpandMoreIcon
                                                      sx={{
                                                        fontSize: "1.2rem",
                                                      }}
                                                    />
                                                  }
                                                  sx={{
                                                    gap: 1.5,
                                                    minHeight:
                                                      "36px !important",
                                                  }}
                                                  slotProps={{
                                                    content: {
                                                      sx: {
                                                        display: "flex",
                                                        flexDirection: "column",
                                                        my: "8px !important",
                                                      },
                                                    },
                                                  }}
                                                >
                                                  <Box
                                                    display="flex"
                                                    justifyContent={
                                                      "space-between"
                                                    }
                                                    alignItems="center"
                                                    gap={0.5}
                                                  >
                                                    <Typography
                                                      variant="body2"
                                                      sx={{
                                                        fontSize: "0.78rem",
                                                        color:
                                                          "rgba(0, 0, 0, 0.8)",
                                                        fontWeight: 500,
                                                        lineHeight: 1.4,
                                                      }}
                                                    >
                                                      {option?.field_label}
                                                    </Typography>
                                                  </Box>
                                                </AccordionSummary>

                                                <AccordionDetails
                                                  sx={{
                                                    pt: 0.5,
                                                    pb: 1,
                                                    px: 1.5,
                                                  }}
                                                >
                                                  <Box
                                                    sx={{
                                                      display: "flex",
                                                      gap: 0.75,
                                                      flexWrap: "wrap",
                                                      alignItems: "center",
                                                      justifyContent:
                                                        "space-between",
                                                    }}
                                                  >
                                                    {option?.location && (
                                                      <Chip
                                                        label={startCase(
                                                          option.location,
                                                        )}
                                                        size="small"
                                                        variant="filled"
                                                        color={
                                                          getLocationStyle(
                                                            option.location,
                                                          ).chipColor
                                                        }
                                                        sx={{
                                                          fontSize: "0.7rem",
                                                          height: 20,
                                                          borderRadius: 1,
                                                        }}
                                                      />
                                                    )}
                                                    <Box
                                                      display="flex"
                                                      alignItems="center"
                                                      gap={1}
                                                      justifyContent="flex-end"
                                                    >
                                                      <IconButton
                                                        size="small"
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          handleAttach(
                                                            e,
                                                            option,
                                                          );
                                                        }}
                                                        disabled={loadingDoc}
                                                        sx={{
                                                          border:
                                                            "1px solid #d0d7de",
                                                          borderRadius: 1.5,
                                                          backgroundColor:
                                                            "transparent",
                                                          p: "4px !important",
                                                        }}
                                                      >
                                                        <AttachFileIcon
                                                          sx={{
                                                            fontSize: "0.85rem",
                                                            transform:
                                                              "rotate(45deg)",
                                                          }}
                                                        />
                                                      </IconButton>
                                                    </Box>
                                                  </Box>
                                                </AccordionDetails>
                                              </Accordion>
                                            ),
                                          )}
                                        </Box>
                                      </Box>
                                    )}
                                </AccordionDetails>
                              </Accordion>
                            ))}
                          </Box>
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                  ),
                )}
              </Stack>
            </Box>
            <Box sx={{ py: 1.5, display: "flex", justifyContent: "center" }}>
              <Pagination
                count={totalPage}
                page={page}
                onChange={handlePageChange}
                siblingCount={0}
                size={"small"}
              />
            </Box>
          </>
        )}
      </Box>

      <Popover
        id={Boolean(filterAnchorEl) ? "simple-popover" : undefined}
        open={Boolean(filterAnchorEl)}
        anchorEl={filterAnchorEl}
        onClose={handleCloseFilter}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        <Stack p={2} gap={1}>
          <Typography sx={{ fontWeight: 500 }}>Filter</Typography>
          <TextField
            label="Location"
            size="small"
            disabled={loading}
            select
            value={filter?.location ?? ""}
            sx={{ minWidth: "200px" }}
            onChange={(event) => {
              setFilter((prev) => ({ ...prev, location: event.target.value }));
            }}
          >
            <MenuItem value="create">Create</MenuItem>
            <MenuItem value="sign">Sign</MenuItem>
          </TextField>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Button
              variant="contained"
              size="small"
              disabled={loading}
              onClick={() => {
                setFilter({});
                getFormFields({}, 1, searchField);
                setPage(1);
                handleCloseFilter();
              }}
              color="primary"
            >
              Clear Filter
            </Button>
            <Button
              variant="contained"
              size="small"
              disabled={loading}
              onClick={() => {
                getFormFields(filter, 1, searchField.trim());
                setPage(1);
                handleCloseFilter();
              }}
              color="primary"
            >
              Apply
            </Button>
          </Box>
        </Stack>
      </Popover>
    </>
  );
};

export default DefualtFormFieldPanel;
